import { catalogue as highfieldCatalogue } from './catalogue';
import { findLibraryCourse } from './libraryCatalogue';

const HANDOFF_PARAM = 'smBasket';
const HIGHFIELD_STORAGE_KEY = 'aptenvo-basket-v1';
const OWN_COURSE_STORAGE_KEY = 'sousa-murray-learning-course-basket-v2';
const MAX_HIGHFIELD_LICENCES = 25;
const MAX_COURSE_LINES = 25;

type StorefrontHandoff = {
  version?: number;
  highfieldItems?: Array<{ courseId?: string; quantity?: number }>;
  ownCourseSlugs?: string[];
};

type NormalisedHandoff = {
  highfieldItems: Array<{ courseId: string; quantity: number }>;
  ownCourseSlugs: string[];
};

function parseHandoff(raw: string): NormalisedHandoff | null {
  let payload: StorefrontHandoff;
  try {
    payload = JSON.parse(raw) as StorefrontHandoff;
  } catch {
    return null;
  }

  if (payload.version !== 1) return null;
  const highfieldItems = Array.isArray(payload.highfieldItems) ? payload.highfieldItems : [];
  const ownCourseSlugs = Array.isArray(payload.ownCourseSlugs) ? payload.ownCourseSlugs : [];

  const highfieldById = new Map(highfieldCatalogue.map((course) => [course.id, course]));
  const combinedHighfield = new Map<string, number>();
  for (const item of highfieldItems) {
    const courseId = String(item?.courseId || '').trim();
    const quantity = Number(item?.quantity);
    if (!highfieldById.has(courseId) || !Number.isInteger(quantity) || quantity < 1 || quantity > MAX_HIGHFIELD_LICENCES) return null;
    combinedHighfield.set(courseId, (combinedHighfield.get(courseId) ?? 0) + quantity);
  }

  const normalisedHighfield = [...combinedHighfield.entries()].map(([courseId, quantity]) => ({ courseId, quantity }));
  const highfieldLicenceCount = normalisedHighfield.reduce((total, item) => total + item.quantity, 0);
  if (highfieldLicenceCount > MAX_HIGHFIELD_LICENCES || normalisedHighfield.some((item) => item.quantity > MAX_HIGHFIELD_LICENCES)) return null;

  const uniqueOwn = [...new Set(ownCourseSlugs.map((slug) => String(slug || '').trim()).filter(Boolean))];
  if (uniqueOwn.length !== ownCourseSlugs.length || uniqueOwn.some((slug) => !findLibraryCourse(slug))) return null;
  if (normalisedHighfield.length + uniqueOwn.length > MAX_COURSE_LINES) return null;
  if (!normalisedHighfield.length && !uniqueOwn.length) return null;

  return { highfieldItems: normalisedHighfield, ownCourseSlugs: uniqueOwn };
}

export type StorefrontBasketHandoffResult = {
  imported: boolean;
  highfieldLicenceCount: number;
  ownCourseCount: number;
  error?: 'invalid_payload' | 'storage_unavailable';
};

export function importStorefrontBasketHandoff(): StorefrontBasketHandoffResult {
  if (typeof window === 'undefined') return { imported: false, highfieldLicenceCount: 0, ownCourseCount: 0 };
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(HANDOFF_PARAM);
  if (!raw) return { imported: false, highfieldLicenceCount: 0, ownCourseCount: 0 };

  const handoff = parseHandoff(raw);
  if (!handoff) return { imported: false, highfieldLicenceCount: 0, ownCourseCount: 0, error: 'invalid_payload' };

  try {
    window.localStorage.setItem(HIGHFIELD_STORAGE_KEY, JSON.stringify(handoff.highfieldItems));
    window.localStorage.setItem(
      OWN_COURSE_STORAGE_KEY,
      JSON.stringify(handoff.ownCourseSlugs.map((courseSlug) => ({ courseSlug }))),
    );
  } catch {
    return { imported: false, highfieldLicenceCount: 0, ownCourseCount: 0, error: 'storage_unavailable' };
  }

  url.searchParams.delete(HANDOFF_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

  return {
    imported: true,
    highfieldLicenceCount: handoff.highfieldItems.reduce((total, item) => total + item.quantity, 0),
    ownCourseCount: handoff.ownCourseSlugs.length,
  };
}
